/**
 * Display Tree / Group Ops Action Definitions
 *
 * Phase 3 of `gibbs-bmaps-capability-coverage`. Binds the 5 tree- and
 * group-management UserActions in `proto/src/cmds/DisplayCmds.js` that
 * the audit reported as unreachable from Gibbs.
 *
 * Every action operates on the BMaps compound tree (the side-bar list
 * of compounds and groups). Preconditions: `hasCompounds` for any op
 * that references a compound or group; `change-tree-sort` is the
 * exception — it works on an empty tree.
 *
 * Note: `GroupCreate`, `GroupRename`, and `GroupDelete` already had
 * Gibbs ActionDefs (`create-compound-group` etc. in
 * `actions/ui-operations.js`). This file fills in the rest.
 *
 * @module gibbs/registry/actions/display-tree-ops
 */

import { param, pre } from '../schema';

// ─── Group composition / hierarchy ─────────────────────────────────────────

export const groupItems = {
    id: 'group-items',
    name: 'Add Items to Group',
    category: 'Display',
    description: 'Add one or more compounds (or other tree items) to an existing group in the compound tree. Used when the user wants to organize compounds into a named bucket they\'ve already created.',
    inputs: [
        param('items', 'CompoundList', 'Compounds to add to the group', true),
    ],
    outputs: [],
    parameters: [
        param('treeName', 'string', 'Which tree to operate on (compound, fragment, etc.)', true, { defaultValue: 'compound' }),
        param('groupName', 'string', 'Target group name', true),
    ],
    dependsOn: [],
    preconditions: [
        pre('compound_loaded', 'A compound must be loaded'),
    ],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'GroupItems',
    serverCommand: null,
    jobType: null,
};

export const groupSort = {
    id: 'group-sort',
    name: 'Sort Group Contents',
    category: 'Display',
    description: 'Sort the compounds inside a tree group either alphabetically or by interaction-score energy. Use when the user wants the bucket re-ordered without dissolving it.',
    inputs: [],
    outputs: [],
    parameters: [
        param('groupName', 'string', 'Target group to sort', true),
        param('sortType', 'string', 'Sort key', true, { options: ['Alphabetical', 'EnergyScore'] }),
    ],
    dependsOn: [],
    preconditions: [
        pre('compound_loaded', 'A compound must be loaded'),
    ],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'GroupSort',
    serverCommand: null,
    jobType: null,
};

export const groupUngroup = {
    id: 'group-ungroup',
    name: 'Ungroup',
    category: 'Display',
    description: 'Dissolve a tree group, returning its members to the parent level of the compound tree. The compounds themselves are not deleted — only the group container is removed.',
    inputs: [],
    outputs: [],
    parameters: [
        param('groupName', 'string', 'Group to dissolve', true),
        param('treeName', 'string', 'Which tree the group lives in', true, { defaultValue: 'compound' }),
    ],
    dependsOn: [],
    preconditions: [
        pre('compound_loaded', 'A compound must be loaded'),
    ],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'GroupUngroup',
    serverCommand: null,
    jobType: null,
};

// ─── Tree-level navigation / sort ──────────────────────────────────────────

export const moveTreeItem = {
    id: 'move-tree-item',
    name: 'Move Tree Item',
    category: 'Display',
    description: 'Move a compound or group to a different position in the compound tree (different parent group, different index). Use for reorganizing the tree without dissolving structure.',
    inputs: [],
    outputs: [],
    parameters: [
        param('treeName', 'string', 'Which tree (compound, fragment, etc.)', true, { defaultValue: 'compound' }),
        param('movingItemId', 'string', 'Identifier for the tree item to move', true),
        param('targetGroupName', 'string', 'Destination group (omit to move to root)', false),
    ],
    dependsOn: [],
    preconditions: [
        pre('compound_loaded', 'A compound must be loaded'),
    ],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'MoveTreeItem',
    serverCommand: null,
    jobType: null,
};

export const changeTreeSort = {
    id: 'change-tree-sort',
    name: 'Change Tree Sort',
    category: 'Display',
    description: 'Change the sort order applied to an entire tree (compound tree, fragment tree, etc.). Affects how all groups and items at the top level of that tree are arranged.',
    inputs: [],
    outputs: [],
    parameters: [
        param('treeName', 'string', 'Which tree to re-sort', true, { defaultValue: 'compound' }),
        param('sortType', 'string', 'Sort key', true, { options: ['Alphabetical', 'EnergyScore'] }),
    ],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'ChangeTreeSort',
    serverCommand: null,
    jobType: null,
};

// ─── Default Export ────────────────────────────────────────────────────────

export default [
    groupItems,
    groupSort,
    groupUngroup,
    moveTreeItem,
    changeTreeSort,
];
